import React from "react"
import { graphql, useFragment } from "react-relay"

import {
  SmartMatchingData,
  SmartMatchingDataPerson,
} from "~/SmartMatching/SmartMatchingContext"
import CollapsibleSection from "./CollapsibleSection"
import {
  isFieldTypeIncluded,
  getFieldScoreByType,
} from "./helpers/fieldScoreResolvers"
import { getScore } from "~/SmartMatching/helpers"
import MatchSectionDetail from "./MatchSectionDetail"
import { TargetPerson } from "./MatchDetailTab"
import { JobTitleSection_person$key } from "./__generated__/JobTitleSection_person.graphql"
import { getCurrentOrLastContract } from "~/helpers/person"

type Props = {
  smartMatchingData: SmartMatchingDataPerson
  embeddingFields: SmartMatchingData["embeddingFields"]
  targetPerson: TargetPerson
  source: JobTitleSection_person$key
}

const personFragment = graphql`
  fragment JobTitleSection_person on people {
    id
    contracts {
      id
      start_date: start_date_runn
      end_date: end_date_runn
      job_title
    }
  }
`

const JobTitleSection = ({
  smartMatchingData,
  embeddingFields,
  targetPerson,
  source,
}: Props) => {
  const sourcePerson = useFragment(personFragment, source)

  if (!isFieldTypeIncluded(embeddingFields, "JOB_TITLE")) {
    return null
  }

  const score = getFieldScoreByType(
    smartMatchingData,
    embeddingFields,
    "JOB_TITLE",
  )

  const scoreLevel = getScore(score)
  const isSimilar =
    scoreLevel?.value === "good" || scoreLevel?.value === "great"

  const latestSourceContract = getCurrentOrLastContract(sourcePerson.contracts)
  const latestTargetContract = getCurrentOrLastContract(targetPerson.contracts)

  const sourceJobTitle = latestSourceContract?.job_title || "No job title"
  const targetJobTitle = latestTargetContract?.job_title || "No job title"

  return (
    <CollapsibleSection
      title="Job Title"
      score={score}
      summary={isSimilar ? "Similar" : ""}
    >
      <MatchSectionDetail
        scoreLevel={scoreLevel}
        sourceDetail={sourceJobTitle}
        targetDetail={`${targetPerson.first_name}: ${targetJobTitle}`}
      />
    </CollapsibleSection>
  )
}

export default JobTitleSection
